import { isValidSessionId } from '@auxcast/protocol';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ConnectionBadge } from '../components/ConnectionBadge';
import { LevelMeters } from '../components/LevelMeters';
import { BufferSlider, VolumeControls } from '../components/MixControls';
import { PasscodeForm } from '../components/PasscodeForm';
import { Shell } from '../components/Shell';
import { StatsGrid } from '../components/StatsGrid';
import type { SessionInfo } from '../net/api';
import { shareableListenUrl } from '../net/sessionLink';
import type { ListenerState } from '../session/listenerController';
import { ListenerController } from '../session/listenerController';
import { useSessionInfo } from '../session/useSessionInfo';

export function ListenPage() {
  const { sessionId } = useParams<{ sessionId: string }>();

  if (!sessionId || !isValidSessionId(sessionId)) {
    return (
      <Shell>
        <div className="center-state">
          <h1>Not a session link</h1>
          <p>
            Session codes are 11 characters long. Check the link you were sent — a truncated one is
            the usual culprit.
          </p>
          <Link className="btn" to="/">
            Back to the start
          </Link>
        </div>
      </Shell>
    );
  }

  return <SessionLoader key={sessionId} sessionId={sessionId} />;
}

function SessionLoader({ sessionId }: { sessionId: string }) {
  const { state, reload } = useSessionInfo(sessionId);

  if (state.status === 'loading') {
    return (
      <Shell>
        <div className="center-state" aria-busy="true">
          <span className="spinner" aria-hidden="true" />
          <p>Looking up the session…</p>
        </div>
      </Shell>
    );
  }

  if (state.status === 'error') {
    const notFound = state.error.kind === 'not-found';
    return (
      <Shell>
        <div className="center-state">
          <h1>{notFound ? 'Session not found' : 'Cannot reach the relay'}</h1>
          <p>
            {notFound
              ? 'This link has expired, or the code is wrong. Sessions are held for two minutes after the sender disconnects.'
              : state.error.message}
          </p>
          <div className="mix">
            <button type="button" className="btn btn-primary" onClick={reload}>
              Try again
            </button>
            <Link className="btn" to="/">
              Back to the start
            </Link>
          </div>
        </div>
      </Shell>
    );
  }

  return <ListenerView sessionId={sessionId} info={state.info} />;
}

function ListenerView({ sessionId, info }: { sessionId: string; info: SessionInfo }) {
  // useState, not useMemo: the controller owns an AudioContext and a socket, and
  // React is free to throw a memo away and recompute it.
  const [controller] = useState(
    () =>
      new ListenerController(sessionId, {
        name: info.name,
        live: info.live,
        listeners: info.listeners,
        format: info.format,
        requiresPasscode: info.requiresPasscode,
      }),
  );

  const state = useSyncExternalStore(controller.subscribe, controller.getSnapshot);

  useEffect(() => () => controller.stop(), [controller]);

  const [starting, setStarting] = useState(false);
  const shareUrl = shareableListenUrl(sessionId);

  const begin = (passcode?: string) => {
    setStarting(true);
    void controller.start(passcode ?? null).finally(() => setStarting(false));
  };

  return (
    <Shell>
      <div className="session-head">
        <div>
          <h1>{state.name}</h1>
          <p className="session-id">{sessionId}</p>
        </div>
        <ConnectionBadge state={state.connection} detail={state.detail} running={state.running} />
      </div>

      {state.running && !state.needsPasscode ? (
        <section className="panel" aria-label="Playback">
          <div className="transport">
            <button
              type="button"
              className="listen-button"
              data-variant="stop"
              onClick={() => controller.stop()}
            >
              <span aria-hidden="true">■</span> Stop
            </button>
            <VolumeControls
              volume={state.volume}
              muted={state.muted}
              onVolume={(value) => controller.setVolume(value)}
              onMuted={(value) => controller.setMuted(value)}
            />
          </div>

          <LevelMeters sample={controller.samplePeaks} active={state.connection === 'live'} />

          <BufferSlider
            targetMs={state.targetMs}
            measuredMs={state.stats.bufferedMs}
            onChange={(value) => controller.setTargetMs(value)}
          />

          {state.connection === 'failed' ? (
            <p className="notice" data-tone="bad" role="alert">
              Disconnected: {state.detail.reason ?? 'the relay closed the connection'}
            </p>
          ) : null}
          <Notices state={state} controller={controller} />
          <StatsGrid stats={state.stats} listeners={state.listeners} rttMs={state.rttMs} />
        </section>
      ) : (
        <section className="panel gate" aria-label="Start listening">
          {state.needsPasscode ? (
            <>
              <p>
                {state.passcodeRejected
                  ? 'That passcode was not accepted. Check it with the sender and try again.'
                  : 'This session is protected. Enter the passcode the sender gave you.'}
              </p>
              <PasscodeForm
                rejected={state.passcodeRejected}
                submitLabel={starting ? 'Starting…' : state.running ? 'Reconnect' : 'Listen'}
                busy={starting}
                onSubmit={(passcode) => {
                  if (state.running) void controller.retryWithPasscode(passcode);
                  else begin(passcode);
                }}
              />
            </>
          ) : (
            <>
              <p>
                {state.live
                  ? 'The sender is streaming now.'
                  : 'Nothing is streaming yet — you can connect anyway and it will start on its own.'}
              </p>
              <button
                type="button"
                className="listen-button"
                onClick={() => begin()}
                disabled={starting}
              >
                <span aria-hidden="true">{starting ? '' : '▶'}</span>
                {starting ? 'Starting…' : 'Listen'}
              </button>
              <p className="hint">
                Browsers only allow audio after you ask for it, so nothing plays until you press
                this.
              </p>
            </>
          )}
          {state.fatal ? (
            <p className="notice" data-tone="bad" role="alert">
              <strong>{state.fatal.code}</strong> — {state.fatal.message}
            </p>
          ) : null}
        </section>
      )}

      <ShareRow url={shareUrl} listeners={state.listeners} running={state.running} />
    </Shell>
  );
}

function Notices({
  state,
  controller,
}: {
  state: ListenerState;
  controller: ListenerController;
}) {
  if (state.fatal) {
    return (
      <p className="notice" data-tone="bad" role="alert">
        <strong>{state.fatal.code}</strong> — {state.fatal.message}
      </p>
    );
  }

  if (state.audioSuspended) {
    return (
      <div className="notice" data-tone="warn">
        <p style={{ margin: '0 0 0.6rem' }}>
          The browser paused audio playback. Resume it to hear the stream.
        </p>
        <button type="button" className="btn" onClick={() => void controller.resumeAudio()}>
          Resume audio
        </button>
      </div>
    );
  }

  if (state.connection === 'waiting') {
    return (
      <p className="notice">
        Connected and waiting for the sender. Leave this open — playback starts by itself.
      </p>
    );
  }

  if (state.connection === 'reconnecting') {
    return (
      <p className="notice" data-tone="warn">
        Lost the connection{state.detail.reason ? ` (${state.detail.reason})` : ''}. Retrying
        automatically.
      </p>
    );
  }

  return null;
}

function ShareRow({
  url,
  listeners,
  running,
}: {
  url: string;
  listeners: number;
  running: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const canCopy = typeof navigator !== 'undefined' && Boolean(navigator.clipboard);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <p className="footnote">
      {running ? `${listeners} listening · ` : ''}
      <span>{url}</span>
      {canCopy ? (
        <>
          {' '}
          <button
            type="button"
            className="btn"
            style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem' }}
            onClick={() => {
              void navigator.clipboard.writeText(url).then(
                () => setCopied(true),
                () => setCopied(false),
              );
            }}
          >
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </>
      ) : null}
    </p>
  );
}
